import { createSlice } from "@reduxjs/toolkit";
import { RootState, StoreDispatch } from "./store";
import { getUserName } from "src/network/graphql/userService";
import { isUUID } from "src/utils";

const userNamesSlice = createSlice({
  name: "userNames",
  initialState: {
    cache: {} as Record<string, string>,
    resolvingIds: [] as string[]
  },
  reducers: {
    setUserNames: (state, action: { payload: Record<string, string> }) => {
      const entries = action.payload;

      Object.entries(entries).forEach(([userId, userName]) => {
        state.cache[userId] = userName;
      });
      state.resolvingIds = state.resolvingIds.filter((id) => !(id in entries));
    },
    addResolvingIds: (state, action: { payload: string[] }) => {
      const newIds = action.payload.filter((id) => !state.resolvingIds.includes(id));

      state.resolvingIds = [...state.resolvingIds, ...newIds];
    }
  }
});

const { setUserNames, addResolvingIds } = userNamesSlice.actions;

export const resolveUserNamesAction = (userIds: string[]) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const { cache, resolvingIds } = getState().userNames;
    const currentUserId = getState().auth.userInfo?.id;

    const uniqueIds = Array.from(new Set(userIds));
    const idsToProcess: string[] = [];
    const immediateUpdates: Record<string, string> = {};

    for (const userId of uniqueIds) {
      if (!userId || cache[userId]) continue;

      if (userId === "SYSTEM") continue;

      if (currentUserId && userId === currentUserId) {
        immediateUpdates[userId] = "You";
        continue;
      }

      if (!isUUID(userId)) {
        immediateUpdates[userId] = "Unknown";
        continue;
      }

      if (!resolvingIds.includes(userId)) {
        idsToProcess.push(userId);
      }
    }

    if (Object.keys(immediateUpdates).length > 0) {
      dispatch(setUserNames(immediateUpdates));
    }

    if (idsToProcess.length === 0) return;

    dispatch(addResolvingIds(idsToProcess));

    const results = await Promise.all(
      idsToProcess.map((userId) =>
        getUserName(userId)
          .then((res) => ({ userId, name: res.data?.get_user_name ?? "Unknown" }))
          .catch(() => ({ userId, name: "Unknown" }))
      )
    );

    const resolved = Object.fromEntries(results.map((r) => [r.userId, r.name]));

    dispatch(setUserNames(resolved));
  };
};

export default userNamesSlice.reducer;
